import { z } from "zod";
import contract from "./venus-lightning-v1.json" with { type: "json" };
export const VENUS_LIGHTNING_CONTRACT = contract;
export const VENUS_LIGHTNING_VERSION = "venus-lightning-v1";
export const venusLightningActionSchema = z.discriminatedUnion("action", [
    z.object({ action: z.literal("discover"), decision: z.string().max(2000).optional(), query: z.string().max(500).optional() }).strict(),
    z.object({ action: z.literal("inspect"), decision: z.string().max(2000).optional(), relation: z.string().min(1).max(500) }).strict(),
    z.object({ action: z.literal("query"), decision: z.string().max(2000).optional(), sql: z.string().min(1).max(20_000),
        row_limit: z.number().int().min(1).max(contract.max_rows).default(contract.default_rows) }).strict(),
    z.object({ action: z.literal("answer"), decision: z.string().max(2000).optional(), evidence: z.array(z.string().min(1)).max(16),
        rows: z.array(z.record(z.string(), z.json())).max(200), text: z.string().max(16_000) }).strict(),
]);
/** Accept already lossless positional rows; object rows require unique, exact keys. */
export function lightningPositionalRows(columns, rows) {
    const unique = new Set(columns).size === columns.length;
    z.array(z.json()).parse(rows);
    return rows.map((row) => {
        if (Array.isArray(row)) {
            if (row.length !== columns.length)
                throw new Error("Lightning row width differs from columns");
            return row;
        }
        if (!unique)
            throw new Error("Lightning cannot recover duplicate column aliases from object rows; use unique aliases");
        if (!row || typeof row !== "object" || Object.keys(row).length !== columns.length ||
            columns.some((column) => !Object.hasOwn(row, column))) {
            throw new Error("Lightning row keys differ from columns");
        }
        return columns.map((column) => row[column]);
    });
}
/** State belongs to a freshly rebuilt, visible provider context; never cache between calls. */
export function lightningMetadataDelta(previous, next) {
    const delta = {};
    for (const key of ["scope", "as_of"]) {
        if (!Object.hasOwn(next, key) && Object.hasOwn(previous, key)) {
            delta[key] = null;
            delete previous[key];
            continue;
        }
        if (Object.hasOwn(next, key) && (!Object.hasOwn(previous, key) ||
            JSON.stringify(previous[key]) !== JSON.stringify(next[key]))) {
            delta[key] = next[key];
            previous[key] = next[key];
        }
    }
    return delta;
}
/** Pure wire serializer shared with the offline renderer. Full receipts remain in guarded tool events. */
export function lightningObservation(input, visibleMetadata = {}) {
    if (input.payload.evidenceUsable === false || input.payload.executionStatus === "not_executed") {
        return { error: { tool_call_id: input.resultId, receipt: input.payload } };
    }
    const { scope: _scope, as_of: _asOf, result_id: _id, truncated: _truncated, ...payload } = input.payload;
    if (input.action !== "query")
        return { ...payload,
            ...(Object.hasOwn(input.payload, "truncated") ? { truncated: input.payload.truncated } : {}),
            ...lightningMetadataDelta(visibleMetadata, input.payload), result_id: input.resultId };
    if (!Array.isArray(payload.columns) || !payload.columns.every((v) => typeof v === "string") ||
        !Array.isArray(payload.rows) || typeof input.truncated !== "boolean") {
        throw new Error("Lightning query result requires columns, rows and authoritative completeness");
    }
    const rows = lightningPositionalRows(payload.columns, payload.rows);
    const { columns, rows: _rows, returned_rows, ...extra } = payload;
    if (returned_rows !== undefined && returned_rows !== rows.length)
        extra.returned_rows = returned_rows;
    // Preserve all other authoritative fields, including total counts and pagination.
    return { ...extra, ...lightningMetadataDelta(visibleMetadata, input.payload), result_id: input.resultId, columns, rows, truncated: input.truncated };
}
